import { IJitsiMeetExternalApi } from '@jitsi/react-sdk/lib/types';
import { getScreenDimensions } from '../utils/helper';
import { store } from '../redux/app/configureStore';

export type CustomIJitsiMeetExternalApi = IJitsiMeetExternalApi  & {getRoomsInfo: () => Promise<any>;} & { _myUserID: string }

export type RecoringType = {
    on: boolean // new recording status - boolean,
    mode: string // recording mode, `local`, `stream` or `file`,
    error: string | undefined // error type if recording fails, undefined otherwise
    transcription: boolean // whether a transcription is active or not
}

const { defaultAudioMute, defaultVideoMute } = store?.getState()?.auth;
console.log("[Log] default audio and video", { defaultAudioMute, defaultVideoMute });

const disabledNotifications = [
    // 'connection.CONNFAIL', // shown when the connection fails,
    // 'dialog.cameraConstraintFailedError', // shown when the camera failed
    // 'dialog.cameraNotSendingData', // shown when there's no feed from user's camera
    'dialog.kickTitle', // shown when user has been kicked
    'dialog.liveStreaming', // livestreaming notifications (pending, on, off, limits)
    'dialog.lockTitle', // shown when setting conference password fails
    // 'dialog.maxUsersLimitReached', // shown when maximmum users limit has been reached
    'dialog.micNotSendingData', // shown when user's mic is not sending any audio
    'dialog.passwordNotSupportedTitle', // shown when setting conference password fails due to password format
    'dialog.recording', // recording notifications (pending, on, off, limits)
    'dialog.remoteControlTitle', // remote control notifications (allowed, denied, start, stop, error)
    'dialog.reservationError',
    // 'dialog.screenSharingFailedTitle', // shown when the screen sharing failed
    // 'dialog.serviceUnavailable', // shown when server is not reachable
    'dialog.thankYou',
    'dialog.sessTerminated', // shown when there is a failed conference session
    'dialog.sessionRestarted', // show when a client reload is initiated because of bridge migration
    'dialog.tokenAuthFailed', // show when an invalid jwt is used
    'dialog.tokenAuthFailedWithReasons', // show when an invalid jwt is used with the reason behind the error
    'dialog.transcribing', // transcribing notifications (pending, off)
    'dialOut.statusMessage', // shown when dial out status is updated.
    'liveStreaming.busy', // shown when livestreaming service is busy
    'liveStreaming.failedToStart', // shown when livestreaming fails to start
    'liveStreaming.unavailableTitle', // shown when livestreaming service is not reachable
    'lobby.joinRejectedMessage', // shown when while in a lobby, user's request to join is rejected
    'lobby.notificationTitle', // shown when lobby is toggled and when join requests are allowed / denied
    'notify.audioUnmuteBlockedTitle', // shown when mic unmute blocked
    'notify.chatMessages', // shown when receiving chat messages while the chat window is closed
    'notify.connectedOneMember', // show when a participant joined
    'notify.connectedThreePlusMembers', // show when more than 2 participants joined simultaneously
    'notify.connectedTwoMembers', // show when two participants joined simultaneously
    'notify.dataChannelClosed', // shown when the bridge channel has been disconnected
    'notify.hostAskedUnmute', // shown to participant when host asks them to unmute
    'notify.invitedOneMember', // shown when 1 participant has been invited
    'notify.invitedThreePlusMembers', // shown when 3+ participants have been invited
    'notify.invitedTwoMembers', // shown when 2 participants have been invited
    'notify.kickParticipant', // shown when a participant is kicked
    'notify.leftOneMember', // show when a participant left
    'notify.leftThreePlusMembers', // show when more than 2 participants left simultaneously
    'notify.leftTwoMembers', // show when two participants left simultaneously
    'notify.linkToSalesforce', // shown when joining a meeting with salesforce integration
    'notify.localRecordingStarted', // shown when the local recording has been started
    'notify.localRecordingStopped', // shown when the local recording has been stopped
    'notify.moderationInEffectCSTitle', // shown when user attempts to share content during AV moderation
    'notify.moderationInEffectTitle', // shown when user attempts to unmute audio during AV moderation
    'notify.moderationInEffectVideoTitle', // shown when user attempts to enable video during AV moderation
    'notify.moderator', // shown when user gets moderator privilege
    'notify.mutedRemotelyTitle', // shown when user is muted by a remote party
    'notify.mutedTitle', // shown when user has been muted upon joining,
    'notify.newDeviceAudioTitle', // prompts the user to use a newly detected audio device
    'notify.newDeviceCameraTitle', // prompts the user to use a newly detected camera
    'notify.noiseSuppressionFailedTitle', // shown when failed to start noise suppression
    'notify.participantWantsToJoin', // shown when lobby is enabled and participant requests to join meeting
    'notify.participantsWantToJoin', // shown when lobby is enabled and participants request to join meeting
    'notify.passwordRemovedRemotely', // shown when a password has been removed remotely
    'notify.passwordSetRemotely', // shown when a password has been set remotely
    'notify.raisedHand', // shown when a participant used raise hand,
    'notify.screenShareNoAudio', // shown when the audio could not be shared for the selected screen
    'notify.screenSharingAudioOnlyTitle', // shown when the best performance has been affected by screen sharing
    'notify.selfViewTitle', // show 'You can always un-hide the self-view from settings'
    'notify.startSilentTitle', // shown when user joined with no audio
    'notify.suboptimalExperienceTitle', // show the browser warning
    'notify.unmute', // shown to moderator when user raises hand during AV moderation
    'notify.videoMutedRemotelyTitle', // shown when user's video is muted by a remote party,
    'notify.videoUnmuteBlockedTitle', // shown when camera unmute and desktop sharing are blocked
    'prejoin.errorDialOut',
    'prejoin.errorDialOutDisconnected',
    'prejoin.errorDialOutFailed',
    'prejoin.errorDialOutStatus',
    'prejoin.errorStatusCode',
    'prejoin.errorValidation',
    // 'recording.busy', // shown when recording service is busy
    // 'recording.failedToStart', // shown when recording fails to start
    'recording.unavailableTitle', // shown when recording service is not reachable
    'toolbar.noAudioSignalTitle', // shown when a broken mic is detected
    'toolbar.noisyAudioInputTitle', // shown when noise is detected for the current microphone
    'toolbar.talkWhileMutedPopup', // shown when user tries to speak while muted
    'transcribing.failed', // shown when transcribing fails
];

const disabledSounds = [
  'ASKED_TO_UNMUTE_SOUND',
  'E2EE_OFF_SOUND',
  'E2EE_ON_SOUND',
  'INCOMING_MSG_SOUND',
  'KNOCKING_PARTICIPANT_SOUND',
  'LIVE_STREAMING_OFF_SOUND',
  'LIVE_STREAMING_ON_SOUND',
  'NO_AUDIO_SIGNAL_SOUND',
  'NOISY_AUDIO_INPUT_SOUND',
  'OUTGOING_CALL_EXPIRED_SOUND',
  'OUTGOING_CALL_REJECTED_SOUND',
  'OUTGOING_CALL_RINGING_SOUND',
  'OUTGOING_CALL_START_SOUND',
  'PARTICIPANT_JOINED_SOUND',
  'PARTICIPANT_LEFT_SOUND',
  'RAISE_HAND_SOUND',
  'REACTION_SOUND',
  'RECORDING_OFF_SOUND',
  'RECORDING_ON_SOUND',
  'TALK_WHILE_MUTED_SOUND'
];

export const getConfigOverwrite = (isHost: boolean) => {
    const configOverwrite = {
        startWithAudioMuted: defaultAudioMute,
        startWithVideoMuted: defaultVideoMute,
        disableModeratorIndicator: true,
        startScreenSharing: false,
        enableEmailInStats: false,
        remoteVideoMenu: false,
        disableAudioLevels: false,
        enableInsecureRoomName: false,
        hideLobbyButton: false,
        toolbarButton: ['microphone', 'camera', 'raisehand', 'desktop', 'hangup', ...(isHost ? ['recording'] : []), ...( getScreenDimensions()?.isMobileScreen ? ['participants-pane'] : [] )],
        prejoinConfig: { enabled: false },
        participantsPane: {
          hideModeratorSettingsTab: true,
          hideMoreActionsButton: true,
          hideMuteAllButton: !isHost
        },
        disablePolls: true,
        buttonsWithNotifyClick: ['hangup', 'end-meeting', 'recording', { key: 'hangup-menu', preventExecution: true }],
        enableClosePage: false,
        disableShortcuts: true,
        disabledNotifications,
        raisedHands: {
          disableLowerHandNotification: true,
          disableNextSpeakerNotification: true, // Specifies whether there is a notification when you are the next speaker in line.
          disableRemoveRaisedHandOnFocus: true, // Specifies whether the raised hand will hide when someone becomes a dominant speaker or not.
        },
        disabledSounds,
        localRecording: {
            disable: true
        },
        deeplinking: {
          disabled: true
        }
      };

      const interfaceConfigOverwrite = {
        DISABLE_JOIN_LEAVE_NOTIFICATIONS: true,
        TOOLBAR_BUTTONS: ['microphone', 'camera', 'raisehand', 'desktop', 'hangup', ...(isHost ? ['recording'] : []), ...( getScreenDimensions()?.isMobileScreen ? ['participants-pane'] : [] )],
        SHOW_BRAND_WATERMARK: false,
        SHOW_POWERED_BY: false,
        DEFAULT_LOGO_URL: '',
        DEFAULT_LOGO_WIDTH: 0,
        AUTO_PIN_LATEST_SCREEN_SHARE: 'remote-only',
        PROVIDER_NAME: 'Katon'
      };

    return { configOverwrite, interfaceConfigOverwrite };
};